import cx from 'classnames'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { motionGroup, slideReveal } from 'src/hooks/useAppear'
import { colors } from 'src/theme'

import { ActionButtonProps } from '../ActionButton'
import { BlockTitleProps } from '../BlockTitle'
import { FigureCardProps } from '../FigureCard'
import { PictureProps } from '../Picture'
import { RichTextProps } from '../RichText'

import * as SC from './styled'

export type FiguresProps = {
  className?: string
  blockTitleProps?: BlockTitleProps
  subtitle?: string
  richTextProps?: RichTextProps
  nextImageProps?: PictureProps
  actionButtonProps?: ActionButtonProps
  cards?: FigureCardProps[]
  color?: string
}

const Figures: FC<FiguresProps> = ({
  className,
  blockTitleProps,
  subtitle,
  richTextProps,
  nextImageProps,
  actionButtonProps,
  cards = [],
  color = colors.yellowPale,
}) => {
  type ComplementHeight = { lastIndexInRow: number; height: number }
  const [complementsHeights, setComplementHeights] = useState<
    ComplementHeight[]
  >([])
  const ref = useRef<HTMLDivElement>(null)

  const changeHeight = useCallback(() => {
    // Wait for next tick to prevent wrong layout computation
    setTimeout(() => {
      const context = ref.current
      const targets = context?.querySelectorAll('[data-height]')

      if (!context || !targets) return

      const newHeights: ComplementHeight[] = []

      let lastOffset = 0
      let maxHeight = 0

      targets.forEach((target, index) => {
        const parent = target.closest('[data-height-parent]')
        const heightRefElement = target.querySelector('[data-height-ref]')

        if (!parent || !heightRefElement) return

        const offsetTop = target
          .closest('[data-height-parent]')
          ?.getBoundingClientRect().top
        const height = heightRefElement.clientHeight

        console.log('changeHeight', { index, offsetTop, height })

        if (offsetTop !== lastOffset) {
          maxHeight = height
          lastOffset = offsetTop ?? 0
          newHeights.push({ lastIndexInRow: index, height: height })
        } else {
          if (newHeights.length <= 0) return

          newHeights[newHeights.length - 1].lastIndexInRow = index
          if (height > maxHeight) {
            maxHeight = height
            newHeights[newHeights.length - 1].height = height
          }
        }
      })
      setComplementHeights(newHeights)
    }, 0)
  }, [])

  useEffect(() => {
    // on load
    changeHeight()
    // on resize
    window.addEventListener('resize', changeHeight)
    return () => {
      window.removeEventListener('resize', changeHeight)
    }
  }, [changeHeight])

  const { motions, variants } = useFiguresMotion()

  return (
    <SC.Container className={cx('Figures', className)} ref={ref}>
      {blockTitleProps && <SC._BlockTitle {...blockTitleProps} />}
      <SC.Wrapper>
        {!!richTextProps && (
          <SC.Secondary {...motions.secondary}>
            {nextImageProps && (
              <SC._Picture {...nextImageProps} variants={variants.picture} />
            )}
            <SC.Content variants={variants.content}>
              {subtitle && <SC.Subtitle>{subtitle}</SC.Subtitle>}
              {richTextProps && <SC._RichText {...richTextProps} />}
              {actionButtonProps && <SC._ActionButton {...actionButtonProps} />}
            </SC.Content>
          </SC.Secondary>
        )}
        {cards.length > 0 && (
          <SC.SliderWrapper {...motions.slider}>
            <SC._Slider
              keenOptions={{
                drag: true,
                slides: { perView: 1.09, spacing: 16 },
                defaultAnimation: {
                  duration: 300,
                },
                breakpoints: {
                  // 600 as in sizes
                  '(min-width: 37.5em)': {
                    slides: { perView: 2.09, spacing: 16 },
                  },
                  // 900 as in sizes
                  '(min-width: 56.25em)': {
                    disabled: true,
                  },
                },
                created: () => {
                  changeHeight()
                },
                destroyed: () => {
                  changeHeight()
                },
                updated: () => {
                  changeHeight()
                },
              }}
              slides={cards.map((card, index) => {
                const complementHeight = complementsHeights.find(
                  (complement) => index <= complement.lastIndexInRow
                )?.height
                return {
                  node: (
                    <SC._FigureCard
                      {...card}
                      key={index}
                      color={color}
                      complementHeight={complementHeight ?? undefined}
                      htmlTag="p"
                      index={index}
                      variants={variants.picture}
                    />
                  ),
                }
              })}
            />
          </SC.SliderWrapper>
        )}
      </SC.Wrapper>
    </SC.Container>
  )
}

const useFiguresMotion = () => {
  const motions = useMemo(
    () => ({
      secondary: motionGroup({}).motionProps,
      slider: motionGroup({ staggerChildren: 0.12 }).motionProps,
    }),
    []
  )
  const variants = useMemo(
    () => ({
      picture: slideReveal({}),
      content: slideReveal({ delay: 0.12 }),
    }),
    []
  )

  return { motions, variants }
}

export default Figures
